import { setACL, setBigGroup } from '@acl/acl.store';
import { HttpClient, HttpParams } from '@angular/common/http';
import { toHttpParams } from '@shared/util';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { forkJoin, Observable } from 'rxjs';
import { BasicConfigService } from '@core/service/basicConfig.service';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  Enterprise,
  User,
  resetEnterprise,
  resetUser,
  setUser,
  UserSelf,
  setUserSelf,
  mergeMask,
  setLoginMethod,
  setVerifyMode
} from '../store';
import { setRole } from '../../layout/layout.store';

interface Menu {
  childMenu?: Menu[];
  groupId: number;
  id: number;
  leaf: number;
  menuName: string;
  operateFlag: number;
  parentMenuName?: string;
  path?: string;
}

export interface SystemItem {
  bankStatementQueryDateRange: boolean;
  settlementPushDateRange: string;
  showAccountLabel: boolean;
  limitAddAccount: boolean;
}
@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    private http: HttpClient,
    private store: Store,
    private readonly router: Router,
    private readonly configService: BasicConfigService
  ) {}

  register(
    username: string,
    nickname: string,
    password: string,
    confirmPassword: string,
    authorizeCode?: string
  ): Observable<{ res: boolean; message: string; data?: { userLogin: string; enterprise?: Enterprise } }> {
    let params = new HttpParams();
    params = params
      .set('userLogin', username)
      .set('nickname', nickname)
      .set('password', btoa(password))
      .set('password2', btoa(confirmPassword));
    if (authorizeCode) {
      params = params.set('authorizecode', authorizeCode);
    }
    return this.http
      .post('/account/register', params)
      .pipe(map((resp: any) => ({ res: resp.userLogin === username, message: resp.message, data: resp })));
  }

  login(username: string, password: string, captchacode?: string, ldapLogin = false): Observable<any> {
    let params = new HttpParams();
    params = params.set('username', username).set('password', btoa(password));
    if (captchacode) {
      params = params.set('captchacode', captchacode);
    }
    const url = ldapLogin ? '/account/ldapLogin' : '/account/login';
    return this.http.post(url, params).pipe(
      tap(res => {
        this.store.dispatch(setUser(res));
        this.store.dispatch(setACL({ acl: res.permissions ?? [] }));
      })
    );
  }

  oaLogin(params): Observable<any> {
    return this.http.get(`/account/login`, { params }).pipe(
      tap(res => {
        this.store.dispatch(setUser(res));
        this.store.dispatch(setACL({ acl: res.permissions ?? [] }));
        return res;
      })
    );
  }

  ssoResetLogin(params: string): Observable<any> {
    return this.http.get(`/account/ssoLogin/getAuthorizationCodeUrl/${params}`, {}).pipe(
      tap(res => {
        return res;
      })
    );
  }

  logoff(): Observable<any> {
    return this.http.get('/account/logout').pipe(
      tap(res => {
        this.store.dispatch(resetUser());
        this.store.dispatch(resetEnterprise());
      })
    );
  }

  ping(): Observable<User> {
    return this.http.get<User>('/account/ping').pipe(
      tap(res => {
        this.store.dispatch(setLoginMethod({ loginMethod: res.loginMethod }));
        this.store.dispatch(setVerifyMode({ verifyMode: res.mobileVerificationCode || false }));
      })
    );
  }

  sendResetEmail(username: string, captchacode: string): Observable<{ res: boolean; message: string }> {
    let params = new HttpParams();
    params = params.set('username', username).set('captchacode', captchacode);
    return this.http
      .post('/account/forgetpassword', params)
      .pipe(map((resp: any) => ({ res: resp.message === 'reset.password.email.sent', message: resp.message })));
  }

  resetPassword(password: string, confirmPassword: string, captchacode: string, verificationCode: string) {
    let params = new HttpParams();
    params = params
      .set('newPassword', btoa(password))
      .set('password2', btoa(confirmPassword))
      .set('captchacode', captchacode)
      .set('verificationcode', verificationCode);
    return this.http
      .post('/account/passwordreset', params)
      .pipe(map((resp: any) => ({ res: resp.message === 'password.reset.ok', message: resp.message })));
  }

  // 获取权限
  roleAndPermission() {
    return forkJoin([
      this.http.get<{ roles: string[]; permissions: string[] }>('/personal/getRolePermissions'),
      this.http.get<{ menu: Menu }>('/enterpriseMenu/findEnterpriseMenu')
    ]).pipe(
      tap(data => {
        let roles = data[0].roles;
        this.store.dispatch(setRole({ roles }));
        if (data[0].permissions && data[0].permissions.length > 0) {
          this.store.dispatch(setACL({ acl: data[0].permissions }));
        } else {
          this.logoff();
          //this.router.navigateByUrl('/funding-board/intercept');
        }
        let bigGroupList: string[] = [];
        data[1].menu.childMenu.map(item => {
          if (item.operateFlag === 1) {
            bigGroupList.push(item.menuName);
          }
          if (item.childMenu.length) {
            item.childMenu.map(childItem => {
              if (childItem.operateFlag === 1) {
                bigGroupList.push(childItem.menuName);
              }
            });
          }
        });
        this.store.dispatch(setBigGroup({ bigGroupList }));
      })
    );
  }

  // 获取账户是不是需要掩码
  getAccountSafeInit() {
    return this.http.get<{ accountNoHide: string }>('/security/account/config').pipe(
      tap(data => {
        this.store.dispatch(mergeMask({ account: data.accountNoHide }));
      })
    );
  }

  // 该接口目前用于人名是不是需要掩码---注意：产品不确定是否有其他客户名称使用该校验进行打码
  getCustomerSafeInit() {
    return this.http.get<{ customerNoHide: string }>('/security/customer/config').pipe(
      tap(data => {
        this.store.dispatch(mergeMask({ customer: data.customerNoHide }));
      })
    );
  }

  // 获取集团下全局配置
  getSystemConfig() {
    return this.http.get<SystemItem>('/security/config').pipe(
      tap(data => {
        const keys = Object.keys(data || {});
        for (const key of keys) {
          if (data[key] !== undefined && data[key] !== null) this.configService.setConfig(key, data[key]);
        }
      })
    );
  }

  // 获取用户级相关配置
  getUserCommonConf() {
    return this.http.get<{ userCommonConf: UserSelf }>('/userConf/getUserCommonConf').pipe(
      tap(data => {
        this.store.dispatch(setUserSelf(data.userCommonConf));
      })
    );
  }

  // 修改用户级相关配置
  updateUserCommonConf(params: UserSelf) {
    return this.http
      .post<{ message: string; msg: string }>(`/userConf/updateUserCommonConf`, toHttpParams(params))
      .pipe(
        map(res => ({
          status: res.message.includes('ok') || res.message.includes('success'),
          msg: res.msg
        }))
      );
  }
}
